import type { UsageByAgent } from '@ralph/contracts'
import clsx from 'clsx'
import { Bot, ChevronDown, ChevronRight, Database, Quote, SearchX, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { formatRate } from '@/components/marketplace/maker/analytics-presentation.ts'
import { Badge, Panel, Spinner } from '@/components/ui/primitives.tsx'
import { useI18n } from '@/i18n/index.ts'
import { type AgentUsageRow, agentUsageRows, knowledgeRows, previewRunsExcluded, usageTotals } from './agent-usage.ts'
import { formatShare } from './storage-presentation.ts'

/*
 * The five plural phrases this panel needs used to live at module scope here, each owning its
 * own forms but with its noun written in English inside a component file. They are copy, so
 * they are `t.activity.agents.*` now (PRD-27 LOC-010) — and the two that were SUBJECTS ("One
 * agent has" / "3 agents have") became whole sentences, because a counted subject and its
 * verb agree with each other and a fragment handed across that boundary breaks in both
 * languages rather than one (LOC-013, D-20).
 */

/**
 * Spend, work and grounding, one row per agent (AGM-120, OBS-017).
 *
 * The screen above this one answers "what did the platform cost". This answers the question an
 * administrator actually acts on once anybody can build an agent, which is *whose* agent cost it
 * — and, in the same row, whether that agent is standing on anything. Those two facts are
 * useless apart: spend alone names the expensive agents, grounding alone names the ungrounded
 * ones, and the finding worth acting on is the agent that is both.
 *
 * Every figure is one the API returned or one derived from two of them in `agent-usage.ts`.
 * There is no groundedness *score* anywhere here, because nothing measures one: what exists is a
 * count of citations, which is the number of times an answer pointed at a passage that was
 * really retrieved. It is reported as that count and never dressed up as a quality rate.
 *
 * The knowledge detail is collapsed by default and costs no extra request — it arrives inside
 * the same usage payload — so opening ten agents in a row is free.
 */
export const AgentUsagePanel = ({
  byAgent,
  windowRuns,
  loading,
  failed,
  days
}: {
  byAgent: UsageByAgent[] | undefined
  /** The window's total run count, from the cards above — the denominator previews are missing from. */
  windowRuns: number | undefined
  loading: boolean
  failed: boolean
  days: number
}) => {
  const [expanded, setExpanded] = useState<string | null>(null)
  // Inside the body, never at module scope: a capture taken at import freezes the locale and
  // keeps rendering the previous language with nothing failing (PRD-27 F-3).
  const i18n = useI18n()
  const { formatCost } = i18n
  const t = i18n.t.activity.agents

  const rows = agentUsageRows(byAgent ?? [])
  const totals = usageTotals(byAgent ?? [])
  const previews = previewRunsExcluded(windowRuns ?? 0, byAgent ?? [])

  return (
    <section className="mt-3">
      <div className="mb-2 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <h2 className="eyebrow">{t.title}</h2>
        {rows.length > 0 && (
          <span className="readout text-fg-subtle">
            {t.summary(i18n, {
              agents: totals.agents,
              runs: totals.runs,
              cost: formatCost(totals.costUsd),
              days
            })}
            {/* Why this does not add up to the Runs card above it. Said here rather than left
                for the reader to work out, or worse, to distrust. */}
            {previews > 0 && ` · ${t.previewsExcluded(i18n, previews)}`}
          </span>
        )}
      </div>

      <Panel className="overflow-hidden">
        {failed && (
          <p role="alert" className="px-4 py-3.5 text-[13px] leading-relaxed text-alert">
            {t.failed}
          </p>
        )}
        {!failed && loading && (
          <p role="status" className="flex items-center gap-2 px-4 py-3.5 text-[13px] text-fg-subtle">
            <Spinner />
            {t.busy}
          </p>
        )}
        {!failed && !loading && rows.length === 0 && (
          <p className="px-4 py-3.5 text-[13px] leading-relaxed text-fg-subtle">{t.empty}</p>
        )}

        {/*
          Said once, above the rows, rather than as a badge on each offender: it is a pattern
          across the deployment, and the row-level evidence (a base with no citations) is already
          inside every panel it applies to.
        */}
        {totals.ungroundedAgents > 0 && (
          <p className="flex items-start gap-2 border-b border-rule bg-alert-soft/40 px-4 py-2.5 text-[12.5px] leading-[1.45] text-fg-muted">
            <TriangleAlert size={12} strokeWidth={2.2} className="mt-[3px] shrink-0 text-alert" aria-hidden />
            <span>{t.ungrounded(i18n, totals.ungroundedAgents)}</span>
          </p>
        )}

        <ul className="divide-y divide-rule">
          {rows.map((row, index) => (
            <li key={row.agentId}>
              <AgentRow
                row={row}
                index={index}
                days={days}
                expanded={expanded === row.agentId}
                onToggle={() => setExpanded((current) => (current === row.agentId ? null : row.agentId))}
              />
            </li>
          ))}
        </ul>
      </Panel>
    </section>
  )
}

const AgentRow = ({
  row,
  index,
  days,
  expanded,
  onToggle
}: {
  row: AgentUsageRow
  index: number
  days: number
  expanded: boolean
  onToggle: () => void
}) => {
  const panelId = `agent-knowledge-${row.agentId}`
  const i18n = useI18n()
  const { formatAgo, formatBytes, formatCost, formatTokens } = i18n
  const t = i18n.t.activity.agents
  const storage = i18n.t.activity.storage
  const bases = knowledgeRows(i18n, row)

  return (
    <div className="rise" style={{ animationDelay: `${Math.min(index, 8) * 35}ms` }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        className="focus-outline flex w-full flex-wrap items-center gap-x-3 gap-y-2 px-3 py-3 text-left transition-colors duration-200 hover:bg-card-solid sm:px-4"
      >
        <span aria-hidden className="flex size-6 shrink-0 items-center justify-center text-fg-subtle">
          {expanded ? <ChevronDown size={13} strokeWidth={2.2} /> : <ChevronRight size={13} strokeWidth={2.2} />}
        </span>

        <span className="min-w-[10rem] flex-1">
          <span className="flex min-w-0 items-center gap-2">
            <Bot size={13} strokeWidth={2} className="shrink-0 text-fg-subtle" aria-hidden />
            <span className="truncate text-[13.5px] font-[550] tracking-[-0.014em]">{row.displayName}</span>
          </span>
          <span className="readout mt-0.5 block text-fg-subtle">
            {i18n.t.activity.runsCount(i18n, row.runs)} ·{' '}
            {t.succeeded(formatRate(i18n, row.successRate) ?? i18n.t.common.none)} ·{' '}
            {t.tokens(formatTokens(row.inputTokens + row.outputTokens))}
            {row.lastRunAt ? ` · ${t.lastRun(formatAgo(row.lastRunAt))}` : ''}
          </span>
        </span>

        {/*
          Grounding as two counted facts, never as a score. The badge appears whenever there is
          something to say — citations happened, or knowledge is attached — and its tone is
          alert only for the combination that is a finding: material attached and none of it
          cited. An agent with nothing attached and nothing cited gets no badge at all, because
          it never failed to ground anything; it was never asked to look.
        */}
        <span className="flex shrink-0 items-center gap-1.5">
          {(row.knowledgeBases.length > 0 || row.citations > 0) && (
            <Badge tone={row.citations > 0 ? 'grounded' : 'alert'}>
              <Quote size={9} strokeWidth={2.6} aria-hidden />
              {t.cited(i18n, row.citations)}
            </Badge>
          )}
          {row.zeroHitRate !== null && row.zeroHitRate > 0 && (
            <Badge tone={row.zeroHitRate >= 0.3 ? 'alert' : 'neutral'}>
              <SearchX size={9} strokeWidth={2.6} aria-hidden />
              {t.emptyRate(formatRate(i18n, row.zeroHitRate) ?? i18n.t.common.none)}
            </Badge>
          )}
        </span>

        <span className="ml-auto flex items-center gap-3">
          <span aria-hidden className="hidden h-[6px] w-[88px] shrink-0 overflow-hidden rounded-full bg-rule lg:block">
            <span style={{ width: `${row.barFill * 100}%` }} className="block h-full rounded-full bg-primary" />
          </span>
          <span className="w-[74px] shrink-0 text-right">
            <span className="readout block font-medium">{formatCost(row.costUsd)}</span>
            <span className="readout block text-fg-subtle">
              {t.ofSpend(formatShare(i18n, row.costShare) ?? i18n.t.common.none)}
            </span>
          </span>
        </span>
      </button>

      {expanded && (
        <div id={panelId} className="border-t border-rule bg-card-solid/50 px-3 py-3 sm:px-4">
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Figure
              label={t.figures.costPerSuccess}
              value={row.costPerSuccess === null ? i18n.t.common.none : formatCost(row.costPerSuccess)}
            />
            <Figure
              label={t.figures.tokensInOut}
              value={`${formatTokens(row.inputTokens)} / ${formatTokens(row.outputTokens)}`}
            />
            <Figure label={t.figures.searches} value={i18n.formatCount(row.searches)} />
            <Figure
              label={t.figures.foundNothing}
              value={
                row.zeroHitRate === null ? t.notEnoughYet : (formatRate(i18n, row.zeroHitRate) ?? i18n.t.common.none)
              }
              muted={row.zeroHitRate === null}
            />
            <Figure
              label={t.figures.failedRuns}
              value={i18n.formatCount(row.failedRuns)}
              tone={row.failedRuns > 0 ? 'alert' : undefined}
            />
          </div>

          <div className="mt-3 flex items-center gap-2.5">
            <h4 className="eyebrow">{t.knowledgeTitle}</h4>
            <span aria-hidden className="h-px flex-1 bg-rule" />
            {row.knowledgeBases.length > 0 && (
              <span className="readout text-fg-subtle">
                {storage.files(i18n, row.knowledgeDocuments)} · {formatBytes(row.knowledgeBytes)}
              </span>
            )}
          </div>

          {bases.length === 0 ? (
            /*
              An unattached agent is not an ungrounded one. It searches whatever the person
              asking may already open — the platform's default — so it can and does cite; there
              is simply no fixed set to hold those citations against, which is why this says so
              rather than reporting a shortfall against a list that does not exist.
            */
            <p className="mt-1.5 text-[12.5px] leading-[1.5] text-fg-subtle">
              {/* "its 1 citations" at one citation was an English plural defect, not a
                  translation question — the count is a real variable and reaches 1 on any
                  quiet agent. Both branches are whole sentences, because the citation count
                  is the subject of the second clause and its verb has to agree with it. */}
              {row.citations > 0 ? t.unattachedWithCitations(i18n, row.citations) : t.unattached}
            </p>
          ) : (
            <ul className="mt-1.5 divide-y divide-rule">
              {bases.map((base) => (
                <li key={base.baseId} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2">
                  <span className="flex min-w-[9rem] flex-1 items-center gap-1.5">
                    <Database size={11} strokeWidth={2.1} className="shrink-0 text-fg-subtle" aria-hidden />
                    {/* A base the reader cannot use keeps its size and verdict, never its name
                        (PRD-26 D-54) — the same rule as the storage report beside it. */}
                    {base.name === null ? (
                      <span className="truncate text-[13px] italic text-fg-muted">{storage.baseYouCannotOpen}</span>
                    ) : (
                      <span className="truncate text-[13px]">{base.name}</span>
                    )}
                  </span>
                  <span className="readout text-fg-subtle">
                    {storage.files(i18n, base.documentCount)} · {formatBytes(base.storageBytes)} ·{' '}
                    {storage.passages(i18n, base.passageCount)}
                  </span>
                  {/* The fixed column exists so the verdicts line up down the list; below `sm`
                      there is no column to line up with, and forcing one broke "1 cited · 1m
                      ago" across two lines. */}
                  <span className="readout shrink-0 text-right sm:w-[112px]">
                    {base.citations > 0 ? (
                      <span className="text-grounded">
                        {t.cited(i18n, base.citations)}
                        {base.lastCitedAt ? ` · ${formatAgo(base.lastCitedAt)}` : ''}
                      </span>
                    ) : (
                      <span className="text-alert">{t.neverCited}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {row.unusedBases > 0 && bases.length > 0 && (
            <p className="mt-2 flex items-start gap-1.5 text-[12.5px] leading-[1.45] text-fg-muted">
              <TriangleAlert size={11} strokeWidth={2.2} className="mt-[3px] shrink-0 text-alert" aria-hidden />
              {/*
                The attachment is the CURRENT one and the citations are the window's, so a base
                attached yesterday reads as never cited and has done nothing wrong. Said plainly,
                because this is the line somebody would otherwise act on.
              */}
              <span>{t.unusedBases(i18n, row.unusedBases, days)}</span>
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/** A small labelled reading, in the same register as the metric cards above the table. */
const Figure = ({ label, value, muted, tone }: { label: string; value: string; muted?: boolean; tone?: 'alert' }) => (
  <span>
    <span className="eyebrow block">{label}</span>
    <span
      className={clsx(
        'mt-0.5 block',
        muted ? 'text-[12px] leading-[1.4] text-fg-subtle' : 'readout text-[14px] font-medium tabular-nums',
        tone === 'alert' && 'text-alert'
      )}
    >
      {value}
    </span>
  </span>
)
